import type { SxProps } from '@mui/material';
import type { CSSObject, Theme as MuiTheme } from '@mui/material/styles';
import type { Theme } from 'hephaestus/core';
import type { TypographyProps } from 'hephaestus/web';

/** Plain style object safe for use inside `sx={[...]}` arrays. */
type ReconFlagsDrawerStyle = CSSObject;
type TypographySx = NonNullable<TypographyProps['sx']>;

export type ReconFlagsDetailDrawerTokens = {
  /**
   * Sit the panel under the 72px app bar. AppBar zIndex is drawer + 1, so a
   * full-height drawer header is covered unless paper starts below the bar.
   */
  drawerRootSx: SxProps;
  /** Horizontal inset for text blocks; body uses `px: 0` so rules and tabs stay full width. */
  blockHorizontalInset: ReconFlagsDrawerStyle;
  summaryTitleRow: SxProps;
  prioritySwatch: (prioritySwatchColor: string) => SxProps;
  metadataRows: SxProps;
  metadataLabel: TypographySx;
  metadataValue: TypographySx;
  metadataTypePill: (bgcolor: string, color: string) => TypographySx;
  sectionDivider: SxProps;
  wireframeNote: TypographySx;
  tabsRoot: SxProps;
  scrollRegion: SxProps;
  notesTabPane: SxProps;
  notesTabHistory: SxProps;
  notesTabCompose: SxProps;
  sectionHeading: TypographySx;
  issueCalloutRoot: SxProps;
  issueCalloutTitleRow: SxProps;
  issueCalloutIcon: SxProps;
  issueCalloutTitle: TypographySx;
  issueCalloutBody: TypographySx;
  overviewRowGrid: SxProps;
  /** Full-bleed rule between overview sections (breaks out of horizontal inset). */
  overviewSectionDivider: SxProps;
  rawJsonPre: SxProps;
  footerStack: SxProps;
  /** Full-width rule between fixed notes and footer actions. */
  footerSectionDivider: SxProps;
  footerRow: ReconFlagsDrawerStyle;
};

/**
 * Module-local layout and surface tokens for the recon flags detail drawer.
 * Isolated here for a future move into the shared component library.
 */
export const getReconFlagsDetailDrawerTokens = (
  theme: Theme,
): ReconFlagsDetailDrawerTokens => {
  /** Issue summary callout — saturated banner, dark border, on-surface typography (design). */
  const issueCalloutFg = theme.color.negative;
  const issueCalloutBg = theme.color.negativeVariant ?? '#e95d7e';
  const issueCalloutBodyColor = theme.color.onSurface ?? '#262626';
  const issueCalloutBorderColor =
    theme.color.negativeVariantPressed ?? '#9b2c3e';

  return {
    drawerRootSx: {
      '& .MuiDrawer-paper': {
        top: 72,
        bottom: 0,
        height: 'auto',
      },
    },
    blockHorizontalInset: {
      px: 2,
      boxSizing: 'border-box',
    },
    summaryTitleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      mb: 1.5,
    },
    prioritySwatch: (prioritySwatchColor: string) => ({
      width: 10,
      height: 10,
      borderRadius: 0.5,
      flexShrink: 0,
      bgcolor: prioritySwatchColor,
    }),
    metadataRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    },
    metadataLabel: {
      color: theme.color.onSurfaceVariant,
      minWidth: 72,
    },
    metadataValue: {
      color: theme.color.onSurface,
      wordBreak: 'break-word',
    },
    metadataTypePill: (bgcolor: string, color: string) => ({
      display: 'inline-block',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      fontWeight: 600,
      bgcolor,
      color,
    }),
    sectionDivider: {
      width: '100%',
      borderTop: `1px solid ${theme.color.outlineVariant}`,
      my: 2,
    },
    wireframeNote: {
      color: theme.color.onSurfaceVariant,
      typography: 'caption',
      lineHeight: 1.45,
    },
    tabsRoot: {
      flexShrink: 0,
      width: '100%',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      bgcolor: theme.color.background,
    },
    scrollRegion: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      pt: 2,
      pb: 1,
    },
    notesTabPane: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    notesTabHistory: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      pt: 2,
      pb: 1,
    },
    notesTabCompose: {
      flexShrink: 0,
      borderTop: `1px solid ${theme.color.outlineVariant}`,
      pt: 1.5,
      pb: 1,
      bgcolor: theme.color.background,
    },
    sectionHeading: {
      mb: 1,
    },
    issueCalloutRoot: {
      p: 1.5,
      borderRadius: 1,
      border: `1px solid ${issueCalloutBorderColor}`,
      bgcolor: issueCalloutBg,
      mb: 0,
    },
    issueCalloutTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 0.75,
    },
    issueCalloutIcon: {
      flexShrink: 0,
      fontSize: 12,
      color: issueCalloutFg,
    },
    issueCalloutTitle: {
      color: issueCalloutFg,
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: 1.35,
      letterSpacing: '0.15px',
    },
    issueCalloutBody: {
      color: issueCalloutBodyColor,
      whiteSpace: 'pre-wrap',
    },
    overviewRowGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
      columnGap: 1.5,
      rowGap: 0.75,
      py: 0.35,
    },
    overviewSectionDivider: ((muiTheme: MuiTheme) => ({
      borderTop: `1px solid ${theme.color.outlineVariant}`,
      my: muiTheme.spacing(2),
      mx: muiTheme.spacing(-2),
      width: `calc(100% + ${muiTheme.spacing(4)})`,
    })) as SxProps,
    rawJsonPre: {
      m: 0,
      p: 1.5,
      borderRadius: 1,
      bgcolor: theme.color.surfaceContainer,
      fontSize: 12,
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    footerStack: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flexShrink: 0,
      minWidth: 0,
    },
    footerSectionDivider: {
      width: '100%',
      borderTop: `1px solid ${theme.color.outlineVariant}`,
    },
    footerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      width: '100%',
    },
  };
};
